import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { subscribeToOrders } from './firebase'
import { STUDY_PROGRAM_IDS, type StudyProgramId } from './studyAccess'

export { STUDY_PROGRAM_IDS as STUDY_ACCESS_PRODUCT_IDS }
export type StudyMembershipStatus = 'loading' | 'member' | 'non-member'

type StudyMembership = {
  status: StudyMembershipStatus
  hasAccess: boolean
}

export function useStudyMembership(
  programId?: StudyProgramId | null,
): StudyMembership {
  const { user, loading: authLoading } = useAuth()
  const [ordersLoaded, setOrdersLoaded] = useState(false)
  const [paidProgramIds, setPaidProgramIds] = useState<Set<StudyProgramId>>(
    new Set(),
  )

  useEffect(() => {
    if (authLoading) return

    if (!user) {
      setPaidProgramIds(new Set())
      setOrdersLoaded(true)
      return
    }

    setOrdersLoaded(false)
    return subscribeToOrders(user.uid, (orders) => {
      const next = new Set<StudyProgramId>()
      for (const order of orders) {
        if (order.status !== 'paid') continue
        for (const item of order.lineItems ?? []) {
          if (STUDY_PROGRAM_IDS.includes(item.productId as StudyProgramId)) {
            next.add(item.productId as StudyProgramId)
          }
        }
      }
      setPaidProgramIds(next)
      setOrdersLoaded(true)
    })
  }, [authLoading, user?.uid])

  if (authLoading || (user && !ordersLoaded)) {
    return { status: 'loading', hasAccess: false }
  }

  if (!user) {
    return { status: 'non-member', hasAccess: false }
  }

  const hasAccess = programId
    ? paidProgramIds.has(programId)
    : paidProgramIds.size > 0

  return {
    status: hasAccess ? 'member' : 'non-member',
    hasAccess,
  }
}
